"use client";

import { useCallback, useEffect, useRef } from "react";
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from "web-vitals";

import { flush, track } from "@/lib/analytics";

/**
 * Remontée des Core Web Vitals.
 *
 * Composant dédié, sans rendu, monté dans le layout racine : les écouteurs
 * de `web-vitals` imposent `"use client"`, et l'isoler ici confine la
 * frontière client à ce composant au lieu de la faire remonter au layout
 * entier.
 *
 * On appelle `web-vitals` directement, et non `useReportWebVitals` de
 * `next/web-vitals` : Next embarque une copie figée de web-vitals v3 dont
 * `onLCP` pose un écouteur `visibilitychange` à **chaque** `keydown`/`click`
 * sans jamais le retirer. Sur un instrument joué au clavier, ça fait des
 * milliers d'écouteurs sur `document` en une minute et demie. La version
 * installée ici retire ses écouteurs une fois LCP figé.
 *
 * Les métriques n'arrivent pas ensemble — FCP et LCP peu après le chargement,
 * INP et CLS tout au long de la vie de la page, souvent à la fermeture. On les
 * **accumule et on les envoie groupées**, comme le fait PostHog nativement :
 * un événement par métrique quadruplerait le volume sans rien apprendre de
 * plus. Ce qui arrive après un envoi forme simplement le lot suivant.
 */

/** Attente maximale avant d'envoyer ce qui a été accumulé. Aligné sur PostHog. */
const FLUSH_DELAY_MS = 5_000;

/**
 * Métriques retenues. Garde le filtre explicite : il documente ce qu'on
 * remonte, et protège d'une future métrique ajoutée à l'abonnement sans
 * qu'on l'ait voulue côté outil d'analyse.
 */
const TRACKED_METRICS = new Set(["LCP", "CLS", "FCP", "INP", "TTFB"]);

export function WebVitals() {
  const metricsRef = useRef<Record<string, number>>({});
  const ratingsRef = useRef<Record<string, string>>({});
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * `web-vitals` n'offre pas de désabonnement : chaque appel à `onLCP` & co
   * ajoute un observateur pour la vie de la page. Le composant est monté une
   * fois dans le layout racine ; ce garde évite de doubler les observateurs
   * si l'effet rejoue (StrictMode en dev, remontage accidentel).
   */
  const subscribedRef = useRef(false);

  const emit = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const metrics = metricsRef.current;

    if (Object.keys(metrics).length === 0) {
      return;
    }

    const ratings = ratingsRef.current;
    metricsRef.current = {};
    ratingsRef.current = {};

    track({ name: "web_vitals", properties: { metrics, ratings } });
  }, []);

  const report = useCallback(
    (metric: Metric) => {
      if (!TRACKED_METRICS.has(metric.name)) {
        return;
      }

      // CLS est un ratio sans unité, les autres sont des millisecondes :
      // arrondir CLS à l'entier écraserait toute sa plage utile.
      metricsRef.current[metric.name] =
        metric.name === "CLS" ? Number(metric.value.toFixed(4)) : Math.round(metric.value);

      if (metric.rating) {
        ratingsRef.current[metric.name] = metric.rating;
      }

      if (timerRef.current === null) {
        timerRef.current = setTimeout(emit, FLUSH_DELAY_MS);
      }
    },
    [emit],
  );

  useEffect(() => {
    if (subscribedRef.current) {
      return;
    }

    subscribedRef.current = true;

    onLCP(report);
    onCLS(report);
    onFCP(report);
    onINP(report);
    onTTFB(report);
  }, [report]);

  useEffect(() => {
    // INP et CLS ne sont définitifs qu'à la fermeture. Le client vide sa file à
    // `pagehide` et s'y est abonné au démarrage, donc avant ce gestionnaire :
    // sans `flush()` explicite, le dernier lot serait mis en file après le
    // vidage, et perdu.
    const handlePageHide = () => {
      emit();
      flush();
    };

    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);

      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, [emit]);

  return null;
}
